import {emitOpenCopilotChatV2} from '@github-ui/copilot-chat-app-embedded/emit-open-chat-event'
import {
  logCopilotChatRouting,
  OpenCopilotChatEvent,
  publishOpenCopilotChat,
  subscribeOpenCopilotChat,
  subscribePreloadCopilotChat,
} from '@github-ui/copilot-chat-events'
import {
  copilotChatHeaderButtonID,
  copilotDockedPanelClosedEvent,
  getCopilotChatPolicyVersion,
  getDockedPanelOwner,
  getOpenCopilotChatVersion,
  requestDockedPanelHandoff,
} from '@github-ui/copilot-chat-helpers/constants'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {getDockedPageReady, subscribeDockedPageReady} from '@github-ui/copilot-chat-helpers/docked-page-store'
import {
  readPersistedChatMinimizedState,
  usePersistedChatOpenState,
} from '@github-ui/copilot-chat-helpers/embedded-chat-open-state'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {GlobalCopilotMenu} from '@github-ui/global-copilot-menu/GlobalCopilotMenu'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {CommentDiscussionIcon, CopilotIcon} from '@primer/octicons-react'
import {ButtonGroup} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import {NavLinkItem} from '../../primitives/NavItemLink'
import baseStyles from '../../primitives/styles.module.css'
import {useShouldLoadCopilotChat, useTopNavConfig} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {LazyGlobalNavAgentsMenu} from '../LazyGlobalNavAgentsMenu'
import {CopilotChatLoader} from './CopilotChatLoader'
import styles from './CopilotItems.module.css'

export type CopilotItemsProps = {
  show?: boolean
  showAgentsButton?: boolean
  copilotApiUrl?: string | undefined
  copilotChatUrl?: string
}

// Matches the modifier rules in packages/react-router/tanstack-router/use-link-click-handler.ts
// so the button only intercepts the clicks the router would have navigated itself.
function isPlainLeftClick(event: React.MouseEvent): boolean {
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  return event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
}

export function CopilotItems({show, showAgentsButton, copilotApiUrl, copilotChatUrl}: CopilotItemsProps) {
  const shouldLoadCopilotChat = useShouldLoadCopilotChat()
  const {loadCopilotChat} = useTopNavConfig()
  const {sendItemClick} = useGlobalNavAnalytics('COPILOT')
  const isImmersiveEmbeddedHeaderButton = isFeatureEnabled('copilot_immersive_embedded_header_button')
  const isChatAppEmbedded = isFeatureEnabled('copilot_chat_app_embedded')
  const isOneCopilotNav = isFeatureEnabled('one_copilot_web_nav')
  const showOneCopilotNav = isOneCopilotNav && showAgentsButton
  const shouldShowEmbeddedChatButton = isChatAppEmbedded && Boolean(copilotChatUrl)
  const shouldShowUnifiedChatButton = isFeatureEnabled('one_copilot_unified_chat_button') && Boolean(copilotChatUrl)
  // Example: a user minimizes Copilot on an issue, then follows a full-page link
  // to a PR or file. The new page forgets that the chat code was already loaded,
  // but this browser tab still remembers that Copilot was minimized. Use that
  // saved state to recreate the minimized bar without another click.
  const shouldRestoreMinimizedChat =
    isFeatureEnabled('copilot_chat_minimize_contextual') && copilotLocalStorage.getMinimizedState()

  // Defer the Copilot chat loader to the client. `useRuntimeEnvironment` returns the server
  // snapshot during SSR and the first hydration render, then re-renders as `isBrowser` on the
  // client — so the loader (and its data fetch) never runs on the server or causes a hydration
  // mismatch.
  const {isBrowser} = useRuntimeEnvironment()
  // Store pending open event to re-dispatch after embedded component loads
  const [pendingOpenEvent, setPendingOpenEvent] = useState<OpenCopilotChatEvent | null>(null)
  const chatAppOpenedPathRef = useRef<string | null>(null)
  // Controls the ChatAppEmbedded panel independently from the CopilotImmersiveEmbedded panel
  const [persistedChatAppOpen, setChatAppOpen] = usePersistedChatOpenState()
  const chatAppOpen = isChatAppEmbedded && persistedChatAppOpen
  const [hasUserOpenedChatApp, setHasUserOpenedChatApp] = useState(false)
  const isRestoredChatAppOpen = chatAppOpen && !hasUserOpenedChatApp
  const embeddedChatTriggerRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null)
  const embeddedChatLauncherRef = useRef<HTMLDivElement>(null)

  // Subscribe to open-copilot-chat events to trigger loading.
  useEffect(() => {
    return subscribeOpenCopilotChat(e => {
      loadCopilotChat()
      const openChatVersion = getCopilotChatPolicyVersion({
        chatAppEmbeddedEnabled: isChatAppEmbedded,
        source: e.payload.source,
      })
      const dockedPanelOwner = getDockedPanelOwner()
      logCopilotChatRouting('open event received', {
        id: e.payload.id,
        intent: e.payload.intent,
        source: e.payload.source,
        newThread: 'newThread' in e.payload ? e.payload.newThread : undefined,
        referenceCount: e.payload.references?.length ?? 0,
        chatAppEmbeddedEnabled: isChatAppEmbedded,
        copilotChatUrlPresent: Boolean(copilotChatUrl),
        selectedVersion: openChatVersion,
        dockedPanelOwner,
        dockedPageReady: getDockedPageReady(),
        currentPath: window.location.pathname,
      })
      // When the chat-app-embedded flag is on, page-specific buttons (PR header,
      // explain file, etc.) should open the ChatApp UI.
      if (openChatVersion === 'v2') {
        logCopilotChatRouting('routing event to Chat v2', {
          id: e.payload.id,
          dockedPanelOwner,
        })
        chatAppOpenedPathRef.current = window.location.pathname
        if (copilotChatUrl && getDockedPageReady()) requestDockedPanelHandoff('chatapp')
        // v2 (ChatAppEmbedded) will handle this open. Emit `copilot.open_copilot_chat`
        // here so the shared event stream (v1 emits from
        // `CopilotChatManager.handleOpenPanelEvent`) covers v2 opens too.
        if (e.payload.source !== 'header') {
          emitOpenCopilotChatV2(e)
        }
        setChatAppOpen(true)
        setPendingOpenEvent(e)
      } else {
        logCopilotChatRouting('routing event to immersive chat', {
          id: e.payload.id,
          dockedPanelOwner,
          willCloseChatApp: dockedPanelOwner !== 'chatapp',
        })
        // Only clear `chatAppOpen` when chatapp hasn't already claimed the
        // retained docked-panel slot. Once chatapp owns the claim the
        // immersive UI won't take it over (see `getDockedPanelOwner()`
        // gate in `DockedCopilotChatPortal`), so hiding chatapp here would
        // leave a header-button reopen with an empty `#copilot-chat-docked-content`
        // element — no surface portals into it.
        if (getDockedPanelOwner() !== 'chatapp') {
          chatAppOpenedPathRef.current = null
          setChatAppOpen(false)
        }
        setPendingOpenEvent(e)
      }
    })
  }, [copilotChatUrl, loadCopilotChat, isChatAppEmbedded, setChatAppOpen])

  useEffect(() => {
    if (!isChatAppEmbedded) return

    const clearExpandedChatOnDockedPage = () => {
      const dockedPageReady = getDockedPageReady()
      const minimized = readPersistedChatMinimizedState()
      const dockedPanelOwner = getDockedPanelOwner()
      const openedPath = chatAppOpenedPathRef.current
      const shouldClear =
        dockedPageReady && !minimized && openedPath !== window.location.pathname && dockedPanelOwner !== 'chatapp'

      logCopilotChatRouting('evaluating docked-page state', {
        dockedPageReady,
        minimized,
        dockedPanelOwner,
        openedPath,
        currentPath: window.location.pathname,
        willCloseChatApp: shouldClear,
      })

      if (shouldClear) {
        chatAppOpenedPathRef.current = null
        setChatAppOpen(false)
      }
    }
    const clearChatOnDockClose = () => {
      // Panel-closed events are global, so only clear ChatApp's persisted open
      // state when ChatApp actually owned the dock — otherwise this would also
      // dismiss a minimized ChatApp when an unrelated immersive-owned panel closes.
      const dockedPanelOwner = getDockedPanelOwner()
      logCopilotChatRouting('docked panel closed', {
        dockedPanelOwner,
        willCloseChatApp: dockedPanelOwner === 'chatapp',
      })
      if (dockedPanelOwner !== 'chatapp') return
      chatAppOpenedPathRef.current = null
      setChatAppOpen(false)
    }

    const unsubscribeDockedPageReady = subscribeDockedPageReady(clearExpandedChatOnDockedPage)
    document.addEventListener(copilotDockedPanelClosedEvent, clearChatOnDockClose)
    clearExpandedChatOnDockedPage()

    return () => {
      unsubscribeDockedPageReady()
      document.removeEventListener(copilotDockedPanelClosedEvent, clearChatOnDockClose)
    }
  }, [isChatAppEmbedded, setChatAppOpen])

  // Subscribe to preload-copilot-chat events to warm up the chat component without opening it.
  useEffect(() => {
    return subscribePreloadCopilotChat(() => {
      loadCopilotChat()
    })
  }, [loadCopilotChat])

  useEffect(() => {
    if (shouldRestoreMinimizedChat) {
      // Keep the chat code loaded for the rest of this page visit. Clicking
      // Restore removes the saved minimized state, but the chat code must remain
      // loaded while either the floating dialog or the right-side panel opens.
      loadCopilotChat()
    }
  }, [loadCopilotChat, shouldRestoreMinimizedChat])

  if (!show) {
    return null
  }

  const handleCopilotIconClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    entryPointId: string = 'copilot-assistive',
  ) => {
    e.preventDefault()
    loadCopilotChat()
    publishOpenCopilotChat({
      id: entryPointId,
      intent: CopilotChatIntents.conversation,
      newThread: true,
      source: 'header',
    })
  }

  const handleOpenEmbeddedChat = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
    embeddedChatTriggerRef.current = e.currentTarget
    setHasUserOpenedChatApp(true)
    loadCopilotChat()
    // Publish an event so the side panel integration (on docked pages) can
    // intercept it and open the docked panel. The subscribeOpenCopilotChat
    // handler routes non-header sources to setChatAppOpen.
    publishOpenCopilotChat({
      id: 'copilot-chat-app',
      intent: CopilotChatIntents.conversation,
    })
  }

  const preloadUnifiedChat = () => {
    // The v1 header link has never preloaded, so only warm the chat bundle when
    // this control would open v2.
    if (getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2') {
      loadCopilotChat()
    }
  }

  const copilotItemProps = {
    href: '/copilot',
    icon: CopilotIcon,
    'aria-label': 'Chat with Copilot',
    analyticsKey: 'COPILOT_CHAT',
    analyticsSubCategory: 'COPILOT',
    copilotApiUrl,
    onClick: handleCopilotIconClick,
  }

  return (
    <>
      <div className="hide-sm hide-md">
        <div className={styles.Wrapper}>
          <ButtonGroup>
            {shouldShowUnifiedChatButton ? (
              <div ref={embeddedChatLauncherRef} style={{display: 'contents'}}>
                <NavLinkItem
                  id={copilotChatHeaderButtonID}
                  href="/copilot"
                  icon={CopilotIcon}
                  aria-label="Chat with Copilot"
                  analyticsKey={e =>
                    isPlainLeftClick(e) &&
                    getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2'
                      ? 'COPILOT_EMBEDDED_CHAT'
                      : 'COPILOT_CHAT'
                  }
                  analyticsSubCategory="COPILOT"
                  onClick={e => {
                    if (!isPlainLeftClick(e)) return
                    // Resolve again because the panel owner can change before a click.
                    const isV2 = getOpenCopilotChatVersion({chatAppEmbeddedEnabled: isChatAppEmbedded}) === 'v2'
                    if (isV2) {
                      handleOpenEmbeddedChat(e)
                    } else {
                      handleCopilotIconClick(e, copilotChatHeaderButtonID)
                    }
                  }}
                  onMouseEnter={preloadUnifiedChat}
                  onFocus={preloadUnifiedChat}
                />
              </div>
            ) : (
              <NavLinkItem {...copilotItemProps} />
            )}
            {showOneCopilotNav ? (
              <LazyGlobalNavAgentsMenu
                copilotApiUrl={copilotApiUrl}
                isOneCopilotNav
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(baseStyles.appHeaderButton, styles.OneCopilotMenu),
                  onClick: () => {
                    sendItemClick('AGENTS_MENU')
                  },
                }}
              />
            ) : isImmersiveEmbeddedHeaderButton ? (
              <GlobalCopilotMenu
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                  onMouseEnter: loadCopilotChat,
                  onFocus: loadCopilotChat,
                  onClick: () => {
                    loadCopilotChat()
                    sendItemClick('COPILOT_MENU')
                  },
                }}
              />
            ) : showAgentsButton ? (
              <LazyGlobalNavAgentsMenu
                copilotApiUrl={copilotApiUrl}
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu),
                  onClick: () => {
                    sendItemClick('AGENTS_MENU')
                  },
                }}
              />
            ) : (
              <GlobalCopilotMenu
                anchorProps={{
                  variant: 'invisible',
                  className: clsx(baseStyles.appHeaderButton, styles.CopilotMenu),
                  onMouseEnter: loadCopilotChat,
                  onFocus: loadCopilotChat,
                  onClick: () => {
                    loadCopilotChat()
                    sendItemClick('COPILOT_MENU')
                  },
                }}
              />
            )}
          </ButtonGroup>
          {shouldShowEmbeddedChatButton && !shouldShowUnifiedChatButton && (
            <div ref={embeddedChatLauncherRef} style={{display: 'contents'}}>
              <NavLinkItem
                id={copilotChatHeaderButtonID}
                icon={CommentDiscussionIcon}
                aria-label="Open Copilot Chat"
                analyticsKey="COPILOT_EMBEDDED_CHAT"
                analyticsSubCategory="COPILOT"
                onClick={handleOpenEmbeddedChat}
                onMouseEnter={loadCopilotChat}
                onFocus={loadCopilotChat}
              />
            </div>
          )}
          {!showOneCopilotNav && isImmersiveEmbeddedHeaderButton && showAgentsButton && (
            <LazyGlobalNavAgentsMenu
              copilotApiUrl={copilotApiUrl}
              anchorProps={{
                variant: 'invisible',
                className: clsx(baseStyles.appHeaderButton, styles.AgentsMenu, styles.AgentsMenuStandalone),
                onClick: () => {
                  sendItemClick('AGENTS_MENU')
                },
              }}
            />
          )}
        </div>
      </div>
      {/*
        The chat loader is gated on `isBrowser` so it (and its data fetch) never runs on the server
        or during the first hydration render, keeping this subtree hydration-safe. The embedded header
        flag can be true during SSR, so without this client gate the loader would fetch on the server.
        Saved minimized state is also a load reason. For example, it recreates the minimized Copilot
        bar after navigating from an issue to a PR with a full page load.
      */}
      {isBrowser &&
      copilotChatUrl &&
      (isImmersiveEmbeddedHeaderButton || shouldLoadCopilotChat || chatAppOpen || shouldRestoreMinimizedChat) ? (
        <CopilotChatLoader
          copilotChatUrl={copilotChatUrl}
          pendingOpenEvent={pendingOpenEvent}
          onPendingEventHandled={() => setPendingOpenEvent(null)}
          chatAppOpen={chatAppOpen}
          onChatAppOpen={() => setChatAppOpen(true)}
          onChatAppClose={() => {
            chatAppOpenedPathRef.current = null
            setChatAppOpen(false)
            const fallbackFocusTarget = embeddedChatLauncherRef.current?.querySelector<
              HTMLAnchorElement | HTMLButtonElement
            >('a, button')
            ;(embeddedChatTriggerRef.current ?? fallbackFocusTarget)?.focus()
          }}
          disableAutoFocus={isRestoredChatAppOpen}
        />
      ) : null}
    </>
  )
}
